import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const NavIndicator = () => {
  return (
    <m.div
      className="absolute bottom-0 left-0 right-0 flex items-center justify-center pointer-events-none"
      dir="ltr"
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <m.div
        variants={{
          hidden: { width: 0, opacity: 0 },
          visible: {
            width: '50%',
            opacity: 1,
            transition: { duration: 0.4, delay: 0.1, ease: 'circOut' },
          },
          exit: {
            width: 0,
            opacity: 0,
            transition: { duration: 0.2, ease: 'easeIn' },
          },
        }}
        className="h-[2px] bg-gradient-to-r from-transparent to-rs-bright"
      />
      <m.div
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: {
            scale: 1,
            opacity: 1,
            transition: { duration: 0.25, type: 'spring', bounce: 0.5 },
          },
          exit: {
            scale: 0,
            opacity: 0,
            transition: { duration: 0.2, delay: 0.1 },
          },
        }}
        className="w-1.5 h-1.5 rounded-full bg-rs-bright shadow-[0_0_4px_#e70a35] z-10 shrink-0 mx-0.5"
      />
      <m.div
        variants={{
          hidden: { width: 0, opacity: 0 },
          visible: {
            width: '50%',
            opacity: 1,
            transition: { duration: 0.4, delay: 0.1, ease: 'circOut' },
          },
          exit: {
            width: 0,
            opacity: 0,
            transition: { duration: 0.2, ease: 'easeIn' },
          },
        }}
        className="h-[2px] bg-gradient-to-l from-transparent to-rs-bright"
      />
    </m.div>
  );
};

export const Navbar: React.FC = () => {
  const { t, toggleLanguage, language, dir } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('#home');
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const navLinks = [
    { key: 'nav.home', to: '#home' },
    { key: 'nav.projects', to: '#projects' },
    { key: 'nav.timeline', to: '#timeline' },
    { key: 'nav.team', to: '#team' },
    { key: 'nav.contact', to: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname === '/') {
        const scrollPosition = window.scrollY + 100;
        for (const link of navLinks) {
          if (link.to.startsWith('#')) {
            const section = document.querySelector(link.to) as HTMLElement;
            if (section) {
              const top = section.offsetTop;
              const height = section.offsetHeight;
              if (scrollPosition >= top && scrollPosition < top + height) {
                setActiveSection(link.to);
              }
            }
          }
        }
      }
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const handleNavClick = (to: string) => {
    setIsOpen(false);
    setActiveSection(to);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(to);
        if (element) {
          const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      }, 300);
    } else {
      setTimeout(() => {
        const element = document.querySelector(to);
        if (element) {
          const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      }, 50);
    }
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${scrolled
        ? 'bg-[#050c15]/80 backdrop-blur-md py-4 border-b border-white/10'
        : 'bg-transparent py-4 lg:py-6'
        }`}
      dir={dir}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div
            className="flex items-center cursor-pointer gap-4 group"
            onClick={() => handleNavClick('#home')}
          >
            <m.div
              initial={{ opacity: 0, x: dir === 'rtl' ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex-shrink-0 flex items-center gap-2"
            >
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 bg-rs-bright blur-[25px] opacity-30 group-hover:opacity-60 transition-opacity duration-500 rounded-full scale-125"></div>
                <img
                  src="/logo.png"
                  alt="RS Logo"
                  width="48"
                  height="48"
                  fetchPriority="high"
                  className="relative h-10 w-auto md:h-14 object-contain drop-shadow-[0_0_8px_rgba(231,10,53,0.5)] group-hover:drop-shadow-[0_0_15px_rgba(231,10,53,0.8)] transition-all duration-300"
                />
              </div>
              <div className="hidden md:flex flex-col">
                <span className="text-xl font-bold text-white tracking-widest font-sans leading-none group-hover:text-rs-bright transition-colors">
                  RED SHADOWS | RS
                </span>
                <span
                  className={`${language === 'ar' ? 'text-xs tracking-normal font-bold' : 'text-[10px] tracking-[0.3em]'} text-gray-400 uppercase leading-none mt-1 group-hover:text-white transition-colors`}
                >
                  {t('nav.digitalTeam')}
                </span>
              </div>
            </m.div>
          </div>
          <div className="hidden lg:flex items-center space-x-2 rtl:space-x-reverse">
            {navLinks.map((link) => {
              const isActive = activeSection === link.to && location.pathname === '/';
              const showIndicator = hoveredKey === link.key || (hoveredKey === null && isActive);

              return (
                <button
                  key={link.key}
                  onClick={() => handleNavClick(link.to)}
                  onMouseEnter={() => setHoveredKey(link.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  className={`relative px-4 py-2 rounded text-sm font-bold tracking-wide transition-all duration-300 bg-transparent ${isActive ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  <span className="relative z-10">{t(link.key)}</span>
                  <AnimatePresence>{showIndicator && <NavIndicator />}</AnimatePresence>
                </button>
              );
            })}
            <div className="w-px h-6 bg-white/10 mx-4" />
            <button
              onClick={toggleLanguage}
              className="p-2 text-gray-400 hover:text-rs-bright transition-colors flex items-center gap-1 font-bold text-xs"
              aria-label="Toggle Language"
            >
              <Globe size={18} />
              <span>{language === 'en' ? 'AR' : 'EN'}</span>
            </button>
          </div>
          <div className="lg:hidden flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="p-2 text-gray-400 hover:text-rs-bright transition-colors"
              aria-label="Toggle Language"
            >
              <span className="text-sm font-bold">{language === 'en' ? 'AR' : 'EN'}</span>
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-white p-2"
              aria-label="Toggle Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden bg-[#050c15] border-b border-rs-maroon/20 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-1">
              {navLinks.map((link) => {
                const isActive = activeSection === link.to && location.pathname === '/';

                return (
                  <button
                    key={link.key}
                    onClick={() => handleNavClick(link.to)}
                    className={`relative block px-3 py-4 w-full text-left rtl:text-right text-base font-bold transition-colors border-b border-white/5 last:border-0 ${isActive ? 'text-rs-bright' : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    {t(link.key)}
                    {isActive && (
                      <m.div
                        layoutId="mobile-indicator"
                        className="absolute left-0 top-0 bottom-0 w-1 bg-rs-bright shadow-[0_0_10px_#e70a35]"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
